import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaUser, FaBuilding, FaEnvelope, FaIdCard, FaUserTag, FaMapMarkerAlt } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { useUserProfile } from '../../hooks/useUserProfile';
import Button from '../common/Button';
import { Input } from '../common/Input';
import PhotoUpload from '../common/PhotoUpload';

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  color: ${({ theme }) => theme.colors.text};
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const IconWrapper = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1rem;
`;

const StyledInput = styled(Input)`
  padding-left: 2.5rem;
  width: 100%;
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: 0.85rem;
  margin-top: 0.25rem;
`;

const SuccessMessage = styled.div`
  color: ${({ theme }) => theme.colors.success};
  font-size: 0.9rem;
  padding: 0.75rem;
  background-color: ${({ theme }) => `${theme.colors.success}10`};
  border-radius: 4px;
  text-align: center;
`;

const PersonalInfoTab: React.FC = () => {
  const { supabaseUser } = useAuth();
  const { 
    userProfile, 
    agencyName, 
    agencyAddress,
    loading, 
    error: profileError, 
    updateUserProfile,
    clearError: clearProfileError
  } = useUserProfile();
  
  const [profileUpdateSuccess, setProfileUpdateSuccess] = useState(false);
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    name: ''
  });

  // Mettre à jour le formulaire lorsque les données du profil sont chargées
  useEffect(() => {
    if (userProfile) {
      setProfileForm({
        first_name: userProfile.first_name || '',
        name: userProfile.name || ''
      });
    }
  }, [userProfile]);

  const handleProfileFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearProfileError();
    setProfileUpdateSuccess(false);
    
    try {
      await updateUserProfile({
        first_name: profileForm.first_name,
        name: profileForm.name
      });
      
      setProfileUpdateSuccess(true);
      
      // Masquer le message de succès après 3 secondes
      setTimeout(() => {
        setProfileUpdateSuccess(false);
      }, 3000);
    } catch (error) {
      // L'erreur est déjà gérée par le hook useUserProfile
    }
  };

  return (
    <Container>
      <SectionTitle>Informations personnelles</SectionTitle>
      
      <FormContainer onSubmit={handleProfileSubmit}>
        <FormGroup>
          <Label htmlFor="agency_id">Agence</Label>
          <InputWrapper>
            <IconWrapper>
              <FaBuilding />
            </IconWrapper>
            <StyledInput
              id="agency_id"
              type="text"
              value={agencyName || ''}
              disabled
              placeholder="Votre agence"
            />
          </InputWrapper>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="agency_address">Adresse de l'agence</Label>
          <InputWrapper>
            <IconWrapper>
              <FaMapMarkerAlt />
            </IconWrapper>
            <StyledInput
              id="agency_address"
              type="text"
              value={agencyAddress || ''}
              disabled
              placeholder="Adresse de l'agence"
            />
          </InputWrapper>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="first_name">Prénom</Label>
          <InputWrapper>
            <IconWrapper>
              <FaUser />
            </IconWrapper>
            <StyledInput
              id="first_name"
              name="first_name"
              type="text"
              value={profileForm.first_name}
              onChange={handleProfileFormChange}
              placeholder="Votre prénom"
            />
          </InputWrapper>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="name">Nom</Label>
          <InputWrapper>
            <IconWrapper>
              <FaIdCard />
            </IconWrapper>
            <StyledInput
              id="name"
              name="name"
              type="text"
              value={profileForm.name}
              onChange={handleProfileFormChange}
              placeholder="Votre nom"
            />
          </InputWrapper>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="email">Email</Label>
          <InputWrapper>
            <IconWrapper>
              <FaEnvelope />
            </IconWrapper>
            <StyledInput
              id="email"
              type="email"
              value={userProfile?.email || supabaseUser?.email || ''}
              disabled
              placeholder="Votre email"
            />
          </InputWrapper>
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="role">Rôle</Label>
          <InputWrapper>
            <IconWrapper>
              <FaUserTag />
            </IconWrapper>
            <StyledInput
              id="role"
              type="text"
              value={userProfile?.role || 'user'}
              disabled
              placeholder="Votre rôle"
            />
          </InputWrapper>
        </FormGroup>
        
        {profileError && <ErrorMessage>{profileError.message}</ErrorMessage>}
        
        {profileUpdateSuccess && (
          <SuccessMessage>
            Vos informations ont été mises à jour avec succès !
          </SuccessMessage>
        )}
        
        {/* Section Photo de profil */}
        <FormGroup>
          <Label>Photo de profil</Label>
          <PhotoUpload
            userId={userProfile?.id || 'demo-user'}
            currentPhoto={userProfile?.photo_url}
            onPhotoUpdate={(photoUrl) => {
              console.log('Photo mise à jour:', photoUrl);
            }}
          />
        </FormGroup>
        
        <Button type="submit" $variant="primary" disabled={loading}>
          {loading ? 'Mise à jour en cours...' : 'Mettre à jour le profil'}
        </Button>
      </FormContainer>
    </Container>
  );
};

export default PersonalInfoTab;
